import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection, AngularFirestoreDocument } from 'angularfire2/firestore';
import { Observable } from 'rxjs/Observable';
import { Globals } from '../globals';

interface SupervisorReport {
    active: number;
    createdAt: any;
    employeeId: string;
    endTime: any;
    productionOrderId: string;
    reportDate: any;
    shipId: string;
    startTime: any;
    updatedAt: any;
    employees: any;
    captureUser?: string;
}

interface SupervisorReportId extends SupervisorReport {
    id: string;
}

interface EmployeeReport {
    activitiesCapture: number;
    activityId: any;
    activityName: string;
    advanceValue?: number;
    createdAt: any;
    employeeId: string;
    employeeName: string;
    endTime: any;
    startTime: any;
    updatedAt: any;
    workShiftId: any;
    workShiftName: any;
}

interface EmployeeReportId extends EmployeeReport {
    id: string;
}

@Injectable()
export class SupervisorReportService {

    supervisorReportCollection: AngularFirestoreCollection<SupervisorReport>;
    supervisorReports: any;

    supervisorReportDocument: AngularFirestoreDocument<SupervisorReportId>;
    supervisorReport: Observable<any>;

    employeeReportCollection: AngularFirestoreCollection<EmployeeReport>;
    employeesReport: any;

    collection: string;
    collectionProductionOrder: string;
    collectionEmployee: string;
    collectionShip: string;

    constructor(private afs: AngularFirestore) {
        this.collection = Globals.SUPERVISOR_REPORT_COLLECTION;
        this.collectionProductionOrder = Globals.PRODUCTION_ORDER_COLLECTION;
        this.collectionEmployee = Globals.EMPLOYEE_COLLECTION;
        this.collectionShip = Globals.SHIP_COLLECTION;
    }

    postSupervisorReport(supervisorReportObject: any) {
        this.supervisorReportCollection = this.afs.collection(this.collection);

        return this.supervisorReportCollection.add(supervisorReportObject).then(function(docRef) {
            // console.log('Document added with ID: ', docRef.id);
            return docRef.id;
        }).catch(function(error) {
            // console.error('Error adding document: ', error);
        });
    }

    getSupervisorReports({limit=0}={}) {
        if(!limit){
            this.supervisorReportCollection = this.afs.collection(this.collection, ref => ref.where('active', '==', 1));
        }else{
            this.supervisorReportCollection = this.afs.collection(this.collection, ref => ref.where('active', '==', 1).orderBy('createdAt', 'desc').limit(limit));
        }

        this.supervisorReports = this.supervisorReportCollection.snapshotChanges().map(actions => {
            return actions.map(a => {
                const id$ = a.payload.doc.id;

                const data = a.payload.doc.data() as SupervisorReport;

                const productionOrderId = data.productionOrderId;
                const employeeId = data.employeeId;
                const shipId = data.shipId;
                const reportDate = data.reportDate;
                const captureUser = data.captureUser;

                return { id$, productionOrderId, employeeId, shipId, reportDate, captureUser };
            });
        }).map(documents => {
            return documents.map(document => {
                const productionOrder = this.afs.doc(this.collectionProductionOrder + '/' + document.productionOrderId).
                                        snapshotChanges().map(doc => {
                    const data = doc.payload.data();
                    const productionOrderName = data.name;
                    return { productionOrderName };
                });

                const employee = this.afs.doc(this.collectionEmployee + '/' + document.employeeId).snapshotChanges().map(doc => {
                    const data = doc.payload.data();
                    const employeeName = data.name + ' ' + data.lastName;
                    return { employeeName };
                });

                const ship = this.afs.doc(this.collectionShip + '/' + document.shipId).snapshotChanges().map(doc => {
                    const data = doc.payload.data();
                    const shipName = data.name;
                    const shipDescription = data.description;
                    return { shipName, shipDescription };
                });

                const employeesRef = this.afs.doc(this.collection + '/' + document.id$).collection('employees')
                                     .snapshotChanges().map(docs => {
                    return docs.map(item => {
                        const id$ = item.payload.doc.id;
                        return { id$ };
                    });
                });

                return Observable.combineLatest(productionOrder, employee, ship, employeesRef,
                                                (productionOrderData, employeeData, shipData, employeesData) => {
                    return {
                        productionOrder: productionOrderData,
                        employee: employeeData,
                        ship: shipData,
                        employees: employeesData
                    };
                }).map(data => {
                    return {
                        productionOrderObj: data.productionOrder,
                        employeeObj: data.employee,
                        shipObj: data.ship,
                        employees: data.employees,
                        ...document
                    };
                });
            });
        }).mergeMap(object => {
            return object.length === 0 ? Observable.of([]) : Observable.combineLatest(object);
        });

        return this.supervisorReports;
    }

    getSupervisorReport(supervisorId: string) {
        this.supervisorReportDocument = this.afs.doc(this.collection + '/' + supervisorId);

        this.supervisorReport = this.supervisorReportDocument.snapshotChanges().map(action => {
            const id = action.payload.id;

            const data = action.payload.data() as SupervisorReport;

            return { id, ...data };
        }).map(document => {
            const productionOrder = this.afs.doc(this.collectionProductionOrder + '/' + document.productionOrderId).
                                    snapshotChanges().map(doc => {
                const data = doc.payload.data();
                const productionOrderName = data.name;
                return { productionOrderName };
            });

            const employee = this.afs.doc(this.collectionEmployee + '/' + document.employeeId).snapshotChanges().map(doc => {
                const data = doc.payload.data();
                const employeeName = data.name + ' ' + data.lastName;
                return { employeeName };
            });

            const ship = this.afs.doc(this.collectionShip + '/' + document.shipId).snapshotChanges().map(doc => {
                const data = doc.payload.data();
                const shipName = data.name;
                const shipDescription = data.description;
                return { shipName, shipDescription };
            });

            return Observable.combineLatest(productionOrder, employee, ship, (productionOrderData, employeeData, shipData) => {
                return {
                    productionOrder: productionOrderData,
                    employee: employeeData,
                    ship: shipData
                };
            }).map(data => {
                return {
                    productionOrderObj: data.productionOrder,
                    employeeObj: data.employee,
                    shipObj: data.ship,
                    ...document
                };
            });
        }).mergeMap(object => Observable.combineLatest(object));

        return this.supervisorReport;
    }

    putSupervisorReport(supervisorReportObject: any, id$: string) {
        this.supervisorReportCollection = this.afs.collection(this.collection);

        return this.supervisorReportCollection.doc(id$).update(supervisorReportObject).then(function() {
            // console.log('Document updated');
        }).catch(function(error) {
            // console.error('Error updating document: ', error);
        });
    }

    /*
    // Method to delete the register from DB
    delSupervisorReport(id$: string) {
        this.supervisorReportDocument = this.afs.doc(this.collection + '/' + id$);

        return this.supervisorReportDocument .delete().then(function() {
            // console.log('Document deleted');
        }).catch(function(error) {
            // console.error('Error deleting document: ', error);
        });
    }
    */

    delSupervisorReport(id$: string) {
        if(!confirm('¿Desea eliminar?')) return;
        this.supervisorReportCollection = this.afs.collection(this.collection);

        return this.supervisorReportCollection.doc(id$).update({ active: 0, updatedAt: new Date() }).then(function() {
            // console.log('Document updated');
        }).catch(function(error) {
            // console.error('Error updating document: ', error);
        });
    }

    postEmployeeReport(employeeReportObject: any, supervisorReportId: string) {
        this.supervisorReportCollection = this.afs.collection(this.collection);

        return this.supervisorReportCollection.doc(supervisorReportId).collection('employees').
                                               add(employeeReportObject).then(function(docRef) {
            // console.log('Document added with ID: ', docRef.id);
            return docRef.id;
        }).catch(function(error) {
            // console.error('Error adding document: ', error);
        });
    }

    getEmployeesReport(supervisorReportId: string) {;
        this.employeeReportCollection = this.afs.doc(this.collection + '/' + supervisorReportId).
            collection('employees', ref => ref.orderBy('createdAt', 'desc'));

        this.employeesReport = this.employeeReportCollection.snapshotChanges().map(actions => {
            return actions.map(a => {
                const id$ = a.payload.doc.id;

                const data = a.payload.doc.data() as EmployeeReport;

                const employeeName = data.employeeName;
                const activityName = data.activityName;

                let workShiftName = '';
                if (data.activitiesCapture === 1) {
                    workShiftName = data.startTime + ' - ' + data.endTime;
                } else {
                    workShiftName = data.workShiftName;
                }

                const employeeId = data.employeeId;
                const startTime = data.startTime;
                const endTime = data.endTime;
                const advanceValue = data.advanceValue;

                return { id$, employeeName, activityName, workShiftName, employeeId, startTime, endTime, advanceValue };
            });
        }).map(object => object);

        return this.employeesReport;
    }

    delEmployeeReport(id$: string, supervisorReportId: string) {
        return this.afs.collection(this.collection).doc(supervisorReportId).collection('employees').doc(id$).delete().then(function() {
            // console.log('Document deleted');
        }).catch(function(error) {
            // console.error('Error deleting document: ', error);
        });
    }

    getSupervisorReportsByReportDate(iReportDate: any) {
        this.supervisorReportCollection = this.afs.collection(this.collection, ref => ref.where('active', '==', 1)
                                          .where('reportDate', '==', iReportDate));

        this.supervisorReports = this.supervisorReportCollection.snapshotChanges().map(actions => {
            return actions.map(a => {
                const id$ = a.payload.doc.id;

                const data = a.payload.doc.data() as SupervisorReport;

                const reportDate = data.reportDate;
                const startTime = data.startTime;
                const endTime = data.endTime;

                return { id$, reportDate, startTime, endTime };
            });
        }).map(documents => {
            return documents.map(document => {
                this.employeeReportCollection = this.afs.doc(this.collection + '/' + document.id$).
                                                collection('employees', ref => ref.orderBy('createdAt', 'desc'));

                const employeesRef: any = this.employeeReportCollection.snapshotChanges().map(actions => {
                    return actions.map(a => {
                        const id$ = a.payload.doc.id;

                        const data = a.payload.doc.data() as EmployeeReport;

                        const employeeId = data.employeeId;
                        const employeeName = data.employeeName;
                        const startTime = data.startTime;
                        const endTime = data.endTime;
                        const activityId = data.activityId;
                        const advanceValue = data.advanceValue;

                        return { id$, employeeId, employeeName, startTime, endTime, activityId, advanceValue };
                    });
                });

                return Observable.combineLatest(employeesRef, (employeesData) => {
                    return {
                        employees: employeesData
                    };
                }).map(data => {
                    return {
                        employees: data.employees,
                        ...document
                    };
                });
            });
        }).mergeMap(object => {
            return object.length === 0 ? Observable.of([]) : Observable.combineLatest(object);
        });

        return this.supervisorReports;
    }

    getSupervisorReportsSinceReportDate(iReportDate: any) {
        this.supervisorReportCollection = this.afs.collection(this.collection, ref => ref.where('active', '==', 1)
                                          .where('reportDate', '>=', iReportDate));

        this.supervisorReports = this.supervisorReportCollection.snapshotChanges().map(actions => {
            return actions.map(a => {
                const id$ = a.payload.doc.id;

                const data = a.payload.doc.data() as SupervisorReport;

                const reportDate = data.reportDate;
                const employeeId = data.employeeId;
                const shipId = data.shipId;
                const productionOrderId = data.productionOrderId;
                const captureUser = data.captureUser;

                return { id$, reportDate, productionOrderId, employeeId, shipId, captureUser };
            });
        }).map(documents => {
            return documents.map(document => {
                const productionOrder = this.afs.doc(this.collectionProductionOrder + '/' + document.productionOrderId).
                                        snapshotChanges().map(doc => {
                    const data = doc.payload.data();
                    const productionOrderName = data.name;
                    return { productionOrderName };
                });

                const employee = this.afs.doc(this.collectionEmployee + '/' + document.employeeId).snapshotChanges().map(doc => {
                    const data = doc.payload.data();
                    const employeeName = data.name + ' ' + data.lastName;
                    return { employeeName };
                });

                const ship = this.afs.doc(this.collectionShip + '/' + document.shipId).snapshotChanges().map(doc => {
                    const data = doc.payload.data();
                    const shipName = data.name;
                    const shipDescription = data.description;
                    return { shipName, shipDescription };
                });

                this.employeeReportCollection = this.afs.doc(this.collection + '/' + document.id$).collection('employees');

                const employeesRef: any = this.employeeReportCollection.snapshotChanges().map(docs => {
                    return docs.map(item => {
                        const id$ = item.payload.doc.id;
                        return { id$ };
                    });
                });

                return Observable.combineLatest(productionOrder, employee, ship, employeesRef,
                                                (productionOrderData, employeeData, shipData, employeesData) => {
                    return {
                        productionOrder: productionOrderData,
                        employee: employeeData,
                        ship: shipData,
                        employees: employeesData
                    };
                }).map(data => {
                    return {
                        productionOrderObj: data.productionOrder,
                        employeeObj: data.employee,
                        shipObj: data.ship,
                        employees: data.employees,
                        ...document
                    };
                });
            });
        }).mergeMap(object => {
            return object.length === 0 ? Observable.of([]) : Observable.combineLatest(object);
        });

        return this.supervisorReports;
    }

}
